// Boss art (ENEMY-REFORGE, P1 section 5). Original procedural designs, authored at final world size
// (the renderer draws bosses at scale 1): feet on y = 0, facing +Z, centred on x = z = 0.
// Character's left hand is +X (facing +Z with +Y up).
//
// Silhouette identities (readable at a glance, before the nameplate):
//   IRONHIDE COLOSSUS  — wide, low, four-pillar knuckle-walking siege beast with furnace stacks; horizontal mass.
//   PRISM SOVEREIGN    — tall, slim biped, needle mask, big crystal shoulder prisms in a V, suspended tuning ring.
//   TYRANT GUARDIAN    — upright armoured soldier, huge asymmetric tower shield (left) and a long cleaver (right).
//   HOLLOW TYRANT      — hunched colossus, broken floating crown, open ribcage with a cracked core, long clawed
//                        arms splayed wide, tattered cloak; no shield, no weapon.
import * as THREE from 'three/webgpu';
import { ArtBuilder, ArtGeo, at, EnemyArt, limb, PAL, plate, PartOpts } from './kit';

type V3 = [number, number, number];
const PI = Math.PI;

const sphere = (r: number, ws = 14, hs = 10) => new THREE.SphereGeometry(r, ws, hs);
const cyl = (rt: number, rb: number, h: number, seg = 10) => new THREE.CylinderGeometry(rt, rb, h, seg, 1);
const oct = () => new THREE.OctahedronGeometry(1, 0);
const cone = (r: number, h: number, seg = 8) => new THREE.ConeGeometry(r, h, seg, 1);
/** a crystal shard: elongated octahedron with its base at the origin, pointing +Y */
const shard = (w: number, h: number, d = w) => new THREE.OctahedronGeometry(1, 0).scale(w, h / 2, d).translate(0, h / 2, 0);

// ============================================================================ IRONHIDE COLOSSUS (~9 m)
function ironhide(): ArtGeo {
  const b = new ArtBuilder();
  const IRON = 0x2c2f38, IRON2 = 0x474b57, PLATE = 0x8d8578, RUST = 0x9a5a2c, COPPER = PAL.copper;
  // ---- body: high armoured chest sloping down to low hindquarters (gorilla/siege-engine stance)
  b.add(at(plate(5.6, 3.6, 4.4, 0.45), 0, 6.0, 0.7, -0.22), { color: IRON });
  b.add(at(plate(4.6, 3.0, 3.4, 0.4), 0, 4.6, -2.4, -0.1), { color: IRON });
  b.add(at(plate(3.6, 1.6, 2.6, 0.3), 0, 3.6, -0.6), { color: IRON2 }); // belly
  // armoured chest front (ivory-ash slabs) and a grille
  b.add(at(plate(4.4, 2.6, 0.6, 0.18), 0, 5.6, 2.95, -0.32), { color: PLATE });
  b.add(at(plate(2.0, 0.9, 0.3, 0.1), 0, 4.15, 3.05, -0.45), { color: RUST });
  for (const y of [5.0, 5.55, 6.1]) b.add(at(plate(2.4, 0.12, 0.2, 0.04), 0, y, 3.32 - (y - 5) * 0.32, -0.32), { color: PAL.ember, glow: true });
  // back hump: layered siege plates
  b.add(at(plate(5.0, 0.8, 3.4, 0.25), 0, 7.9, -0.1, -0.18), { color: PLATE });
  b.add(at(plate(4.4, 0.7, 2.6, 0.22), 0, 6.6, -2.6, -0.05), { color: PLATE });
  b.add(at(plate(3.6, 0.5, 2.0, 0.18), 0, 7.1, -2.2, -0.08), { color: IRON2 });
  // furnace stacks (tallest silhouette points) with ember mouths
  for (const s of [-1, 1]) {
    b.add(at(cyl(0.5, 0.62, 2.0), s * 1.3, 8.65, -1.05, -0.25, 0, s * -0.12), { color: IRON2 });
    b.add(at(cyl(0.66, 0.66, 0.35), s * 1.36, 9.55, -1.3, -0.25, 0, s * -0.12), { color: COPPER });
    b.add(at(cyl(0.42, 0.42, 0.12), s * 1.4, 9.75, -1.35, -0.25, 0, s * -0.12), { color: PAL.ember, glow: true });
  }
  // ---- low helmet head, thrust forward between the shoulders (reads from the high game camera)
  const hd = { add: (g: THREE.BufferGeometry, o: PartOpts) => b.add(g.translate(0, 0.35, 0.9), o) };
  hd.add(limb([0, 5.0, 2.4], [0, 5.0, 3.6], 0.9, 0.9, 10), { color: IRON }); // neck collar
  hd.add(at(sphere(1.0), 0, 4.9, 3.5, 0, 0, 0, [1.45, 0.95, 1.4]), { color: IRON2 });
  hd.add(at(plate(3.0, 0.6, 2.6, 0.2), 0, 5.6, 3.6, -0.15), { color: PLATE }); // helmet brim/dome cap
  hd.add(at(plate(0.7, 0.55, 2.6, 0.15), 0, 6.0, 3.3, -0.12), { color: RUST }); // crest ridge
  hd.add(at(plate(2.4, 0.9, 1.0, 0.2), 0, 4.15, 4.3, 0.2), { color: IRON }); // jaw guard
  for (const s of [-1, 1]) {
    hd.add(at(plate(0.75, 0.16, 0.2, 0.05), s * 0.6, 5.08, 4.9, 0, 0, s * -0.12), { color: PAL.ember, glow: true });
    hd.add(at(plate(0.5, 0.9, 1.8, 0.12), s * 1.55, 5.2, 3.5, 0, 0, s * -0.25), { color: PLATE }); // helmet cheek flaps
    // forward ram tusks
    hd.add(limb([s * 0.95, 4.2, 4.3], [s * 1.25, 4.6, 5.9], 0.32, 0.05, 8), { color: PLATE });
  }
  // ---- front pillar limbs (fist-feet) — the "Forgefoot": right one lifts on the wind-up
  for (const s of [-1, 1]) {
    const piv: V3 = [s * 3.0, 6.7, 1.6];
    const o: PartOpts = { color: IRON, pivot: piv, swing: 0.17, sphase: s > 0 ? 0 : PI, raise: s < 0 ? -0.55 : 0 };
    const sx = s * 3.35;
    b.add(limb([s * 3.1, 6.6, 1.6], [sx, 3.2, 2.0], 1.05, 0.95, 12), o);
    b.add(limb([sx, 3.2, 2.0], [sx, 1.2, 2.1], 0.95, 1.05, 12), o);
    b.add(at(sphere(1.0, 12, 8), sx, 3.25, 2.05), { ...o, color: COPPER }); // elbow drum
    b.add(at(cyl(1.25, 1.4, 1.25, 12), sx, 0.62, 2.15), { ...o, color: IRON2 }); // forgefoot
    b.add(at(cyl(1.45, 1.45, 0.25, 12), sx, 0.12, 2.15), { ...o, color: PLATE });
    b.add(at(cyl(1.3, 1.3, 0.14, 12), sx, 1.3, 2.15), { ...o, color: RUST });
    b.add(at(plate(1.0, 0.18, 0.15, 0.05), sx, 0.7, 3.5), { ...o, color: PAL.ember, glow: true });
    // shoulder pauldron: three stacked slabs
    b.add(at(plate(2.6, 1.0, 3.0, 0.3), s * 3.4, 7.3, 1.5, -0.1, 0, s * -0.35), { ...o, color: PLATE });
    b.add(at(plate(2.2, 0.8, 2.6, 0.25), s * 3.85, 6.6, 1.5, -0.1, 0, s * -0.55), { ...o, color: IRON2 });
    b.add(at(plate(1.6, 1.4, 2.0, 0.2), sx, 4.6, 2.2, 0, 0, 0), { ...o, color: PLATE }); // forearm guard
  }
  // ---- rear pillar legs
  for (const s of [-1, 1]) {
    const piv: V3 = [s * 2.0, 4.4, -3.0];
    const o: PartOpts = { color: IRON, pivot: piv, swing: 0.22, sphase: s > 0 ? PI : 0 };
    const sx = s * 2.25;
    b.add(limb([s * 2.0, 4.6, -3.0], [sx, 2.2, -3.4], 1.0, 0.85, 12), o);
    b.add(limb([sx, 2.2, -3.4], [sx, 0.9, -3.2], 0.85, 0.95, 12), o);
    b.add(at(sphere(0.95, 12, 8), sx, 2.3, -3.35), { ...o, color: COPPER });
    b.add(at(cyl(1.1, 1.25, 1.0, 12), sx, 0.5, -3.2), { ...o, color: IRON2 });
    b.add(at(cyl(1.3, 1.3, 0.2, 12), sx, 0.1, -3.2), { ...o, color: PLATE });
    b.add(at(plate(2.0, 1.6, 2.4, 0.25), s * 2.45, 4.4, -3.1, 0, 0, s * -0.25), { ...o, color: PLATE }); // hip plate
  }
  return b.build();
}

// ============================================================================ PRISM SOVEREIGN (~6.5 m)
function prism(): ArtGeo {
  const b = new ArtBuilder();
  const BODY = 0x3a4258, DARK = 0x1f2436, CRY = 0x5fd8e8, CRY2 = 0x9ff0ff, IVORY = PAL.ivory, BRASS = 0xc89a48;
  // ---- long straight legs (planted, measured stride)
  for (const s of [-1, 1]) {
    const piv: V3 = [s * 0.5, 3.15, 0];
    const o: PartOpts = { color: BODY, pivot: piv, swing: 0.26, sphase: s > 0 ? 0 : PI };
    b.add(limb([s * 0.5, 3.2, 0], [s * 0.62, 1.75, 0.15], 0.34, 0.24, 8), o);
    b.add(limb([s * 0.62, 1.75, 0.15], [s * 0.66, 0.3, -0.05], 0.22, 0.14, 8), { ...o, color: DARK });
    b.add(at(shard(0.26, 0.9, 0.2), s * 0.62, 1.4, 0.4, 0.15), { ...o, color: CRY }); // knee crystal
    b.add(at(oct(), s * 0.66, 0.2, 0.25, 0, 0, 0, [0.28, 0.2, 0.75]), { ...o, color: IVORY }); // pointed foot
    b.add(at(plate(0.5, 0.9, 0.12, 0.04), s * 0.82, 2.6, 0.02, 0, 0, s * 0.15), { ...o, color: IVORY }); // thigh plate
  }
  // pelvis + narrow front / back tabards
  b.add(at(plate(1.4, 0.55, 0.75, 0.12), 0, 3.3, 0), { color: DARK });
  b.add(at(plate(0.5, 1.9, 0.08, 0.03), 0, 2.2, 0.36, 0.06), { color: PAL.robe });
  b.add(at(plate(0.22, 1.7, 0.1, 0.03), 0, 2.3, 0.42, 0.06), { color: BRASS });
  b.add(at(plate(1.1, 2.3, 0.08, 0.03), 0, 2.15, -0.42, -0.12), { color: PAL.robe });
  // ---- inverted-prism torso (narrow waist, broad chest)
  b.add(at(cyl(1.0, 0.42, 1.9, 6), 0, 4.45, 0, 0, PI / 6, 0, [1, 1, 0.62]), { color: BODY });
  b.add(at(cyl(0.7, 0.3, 1.2, 6), 0, 4.55, 0.18, 0, PI / 6, 0, [1, 1, 0.5]), { color: IVORY });
  b.add(at(oct(), 0, 4.75, 0.5, 0, 0, 0, [0.18, 0.32, 0.12]), { color: CRY2, glow: true });
  // neck + tall narrow ceremonial mask
  b.add(limb([0, 5.3, 0], [0, 5.6, 0.05], 0.16, 0.13), { color: DARK });
  b.add(at(oct(), 0, 6.0, 0.1, 0, 0, 0, [0.38, 0.85, 0.3]), { color: IVORY });
  b.add(at(plate(0.08, 0.8, 0.06, 0.02), 0, 6.0, 0.36), { color: CRY2, glow: true });
  b.add(at(plate(0.44, 0.06, 0.06, 0.02), 0, 6.1, 0.33), { color: CRY2, glow: true });
  b.add(at(shard(0.08, 0.5, 0.06), 0, 6.75, 0.1), { color: BRASS }); // mask spire
  // ---- angular shoulder prisms (wide V) — tilt up on the wind-up
  for (const s of [-1, 1]) {
    const piv: V3 = [s * 0.95, 5.05, 0];
    const o: PartOpts = { color: CRY, pivot: piv, raise: -0.35 };
    b.add(at(plate(0.75, 0.3, 0.75, 0.08), s * 1.05, 5.1, 0, 0, 0, s * -0.3), { ...o, color: DARK });
    b.add(at(shard(0.42, 2.0, 0.34), s * 1.25, 5.0, -0.05, 0, 0, s * -0.55), o);
    b.add(at(shard(0.24, 1.2, 0.22), s * 1.5, 4.8, 0.25, 0.2, 0, s * -0.95), { ...o, color: CRY2 });
    b.add(at(shard(0.2, 0.9, 0.18), s * 1.1, 4.95, -0.35, -0.35, 0, s * -0.25), o);
    // thin arms with crystal-blade hands; raised forward when conducting the apparatus
    const ao: PartOpts = { color: BODY, pivot: [s * 1.0, 4.95, 0], swing: 0.22, sphase: s > 0 ? PI : 0, raise: -0.9 };
    b.add(limb([s * 1.0, 4.95, 0], [s * 1.2, 3.85, 0.12], 0.17, 0.14), ao);
    b.add(limb([s * 1.2, 3.85, 0.12], [s * 1.28, 2.8, 0.35], 0.14, 0.11), { ...ao, color: DARK });
    b.add(at(plate(0.3, 0.6, 0.22, 0.06), s * 1.22, 3.5, 0.2), { ...ao, color: IVORY });
    b.add(at(shard(0.12, 0.8, 0.06), s * 1.3, 2.85, 0.38, PI), { ...ao, color: CRY });
  }
  // ---- suspended mechanical tuning apparatus: brass ring + fork prongs + orbit crystals, floating behind the head
  const ap: PartOpts = { color: BRASS, pivot: [0, 4.6, -0.8], raise: 0.45 };
  b.add(at(new THREE.TorusGeometry(1.15, 0.07, 6, 28), 0, 5.75, -1.0), ap);
  b.add(at(new THREE.TorusGeometry(0.75, 0.05, 6, 22), 0, 5.75, -1.0), { ...ap, color: 0x8a6a30 });
  for (const s of [-1, 1]) {
    b.add(at(plate(0.12, 1.05, 0.12, 0.03), s * 0.32, 6.78, -1.0), ap);
    b.add(at(shard(0.12, 0.4, 0.1), s * 0.32, 7.3, -1.0), { ...ap, color: CRY2, glow: true });
  }
  b.add(at(plate(0.76, 0.12, 0.14, 0.03), 0, 6.25, -1.0), ap);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * PI * 2 + PI / 6;
    b.add(at(oct(), Math.cos(a) * 1.15, 5.75 + Math.sin(a) * 1.15, -1.0, 0, 0, a, [0.12, 0.26, 0.12]), { ...ap, color: CRY });
  }
  b.add(at(oct(), 0, 5.75, -1.0, 0, 0, 0, [0.22, 0.3, 0.22]), { ...ap, color: CRY2, glow: true });
  return b.build();
}

// ============================================================================ TYRANT GUARDIAN (~6 m)
function guardian(): ArtGeo {
  const b = new ArtBuilder();
  const STEEL = 0x4a5266, DARK = 0x262b38, IVORY = PAL.ivory, SHADE = PAL.ivoryShade, CRIM = 0x8a2234, GOLD = 0xc8a04a;
  // authored compact, then legs stretched (K) and everything above the hips lifted (U) for a towering stance
  const U = 0.25, K = (2.45 + U) / 2.45;
  const lift = (o: PartOpts): PartOpts => (o.pivot ? { ...o, pivot: [o.pivot[0], o.pivot[1] + U, o.pivot[2]] } : o);
  const L = { add: (g: THREE.BufferGeometry, o: PartOpts) => b.add(g.scale(1, K, 1), o.pivot ? { ...o, pivot: [o.pivot[0], o.pivot[1] * K, o.pivot[2]] } : o) };
  const u = { add: (g: THREE.BufferGeometry, o: PartOpts) => b.add(g.translate(0, U, 0), lift(o)) };
  // ---- armoured legs, upright stance
  for (const s of [-1, 1]) {
    const piv: V3 = [s * 0.72, 2.45, 0];
    const o: PartOpts = { color: DARK, pivot: piv, swing: 0.28, sphase: s > 0 ? 0 : PI };
    L.add(limb([s * 0.72, 2.5, 0], [s * 0.8, 1.35, 0.12], 0.42, 0.34), o);
    L.add(limb([s * 0.8, 1.35, 0.12], [s * 0.82, 0.3, 0], 0.34, 0.3), o);
    L.add(at(plate(0.75, 0.9, 0.7, 0.15), s * 0.8, 0.85, 0.12), { ...o, color: STEEL }); // greave
    L.add(at(plate(0.6, 0.55, 0.3, 0.1), s * 0.8, 1.45, 0.42, -0.2), { ...o, color: IVORY }); // knee guard
    L.add(at(plate(0.8, 0.32, 1.35, 0.1), s * 0.82, 0.16, 0.18), { ...o, color: STEEL }); // sabaton
    L.add(at(plate(0.75, 1.0, 0.18, 0.06), s * 0.75, 2.05, 0.55, 0.12, 0, s * 0.08), { ...o, color: SHADE }); // tasset
  }
  // ---- heavy torso
  u.add(at(plate(1.7, 0.9, 1.1, 0.2), 0, 2.75, 0), { color: DARK });
  u.add(at(plate(2.7, 1.9, 1.55, 0.35), 0, 3.95, 0.02), { color: STEEL });
  u.add(at(plate(2.2, 1.5, 0.3, 0.15), 0, 3.95, 0.78, -0.05), { color: IVORY }); // breastplate
  u.add(at(plate(0.32, 2.3, 0.2, 0.05), 0.15, 3.95, 0.95, -0.05, 0, -0.75), { color: CRIM }); // diagonal sash
  u.add(at(plate(1.9, 0.35, 1.3, 0.12), 0, 5.0, 0), { color: DARK }); // gorget
  u.add(at(plate(1.0, 1.8, 0.12, 0.04), 0, 2.4, -0.62, -0.15), { color: CRIM }); // rear tabard
  // ---- helmet with tall forward-swept crest
  u.add(at(cyl(0.48, 0.55, 0.95, 10), 0, 5.55, 0.05), { color: STEEL });
  u.add(at(plate(1.1, 0.35, 1.2, 0.1), 0, 5.2, 0.12), { color: STEEL }); // cheek guards
  u.add(at(plate(0.75, 0.1, 0.1, 0.03), 0, 5.55, 0.58), { color: PAL.teal, glow: true });
  u.add(at(plate(0.14, 0.9, 1.4, 0.05), 0, 6.25, -0.05, 0.25), { color: CRIM });
  u.add(at(plate(0.1, 0.25, 0.9, 0.03), 0, 6.05, 0.4, -0.2), { color: GOLD });
  // ---- right arm (−X) + long cleaver held up and out
  {
    const o: PartOpts = { color: DARK, pivot: [-1.45, 4.55, 0], swing: 0.16, sphase: 0, raise: -0.35 };
    u.add(at(plate(1.4, 0.75, 1.5, 0.25), -1.6, 4.85, 0, 0, 0, 0.3), { ...o, color: IVORY });
    u.add(at(plate(1.2, 0.6, 1.3, 0.2), -1.85, 4.35, 0, 0, 0, 0.45), { ...o, color: STEEL });
    u.add(limb([-1.55, 4.5, 0], [-1.8, 3.35, 0.3], 0.34, 0.3), o);
    u.add(limb([-1.8, 3.35, 0.3], [-1.75, 2.7, 1.05], 0.32, 0.3), { ...o, color: STEEL });
    u.add(at(sphere(0.32, 10, 8), -1.75, 2.65, 1.12), { ...o, color: DARK }); // fist
    // cleaver: grip in the fist, blade sweeps up past the shoulder and out
    const g0: V3 = [-1.75, 2.15, 1.05], g1: V3 = [-1.75, 3.3, 1.2];
    u.add(limb(g0, g1, 0.09, 0.09, 6), { ...o, color: 0x3a2a20 });
    u.add(at(plate(0.6, 0.18, 0.3, 0.05), -1.75, 3.25, 1.18), { ...o, color: GOLD }); // guard
    const bl = at(plate(0.95, 3.9, 0.14, 0.06), 0, 0, 0);
    bl.translate(0.12, 2.0, 0); // blade's base at origin, spine on +x side
    u.add(at(bl, -1.75, 3.3, 1.2, 0.28, 0, 0.42), { ...o, color: 0xb8c0cc });
    const edge = at(plate(0.18, 3.7, 0.16, 0.04), 0, 0, 0); edge.translate(-0.42, 2.0, 0);
    u.add(at(edge, -1.75, 3.3, 1.2, 0.28, 0, 0.42), { ...o, color: CRIM });
    const tip = cone(0.48, 0.8, 4); tip.rotateY(PI / 4); tip.scale(1, 1, 0.15); tip.translate(0.12, 4.3, 0);
    u.add(at(tip, -1.75, 3.3, 1.2, 0.28, 0, 0.42), { ...o, color: 0xb8c0cc });
  }
  // ---- left arm (+X) + enormous asymmetric tower shield (tips forward to charge on the wind-up)
  {
    const sp: V3 = [1.8, 1.0, 1.2];
    const o: PartOpts = { color: DARK, pivot: sp, swing: 0.05, sphase: PI, raise: 0.38 };
    u.add(at(plate(1.4, 0.8, 1.5, 0.25), 1.6, 4.85, 0, 0, 0, -0.3), { color: IVORY, pivot: [1.45, 4.5, 0], swing: 0.05, sphase: PI });
    u.add(limb([1.55, 4.5, 0], [1.8, 3.4, 0.4], 0.34, 0.3), { color: DARK, pivot: [1.45, 4.5, 0], swing: 0.05, sphase: PI });
    // the shield: tall slab, uneven top (high outer horn, notched inner lance-rest), pointed foot
    const Z = 1.3, X = 2.1;
    u.add(at(plate(2.5, 4.1, 0.3, 0.1), X, 3.05, Z, 0, -0.18, 0), { ...o, color: IVORY });
    u.add(at(plate(2.7, 4.3, 0.22, 0.1), X, 3.05, Z - 0.12, 0, -0.18, 0), { ...o, color: STEEL }); // rim
    u.add(at(plate(1.0, 1.5, 0.28, 0.08), X + 0.6, 5.35, Z - 0.1, 0, -0.18, -0.18), { ...o, color: STEEL }); // outer horn
    u.add(at(shard(0.38, 1.2, 0.14), X + 0.78, 5.9, Z - 0.12, 0, -0.18, -0.25), { ...o, color: IVORY });
    u.add(at(plate(0.7, 0.55, 0.26, 0.08), X - 0.65, 5.05, Z - 0.05, 0, -0.18, 0.2), { ...o, color: STEEL }); // inner step
    const foot = cone(1.15, 1.1, 4); foot.rotateY(PI / 4); foot.rotateX(PI); foot.scale(1, 1, 0.16);
    u.add(at(foot, X, 0.75, Z - 0.1, 0, -0.18, 0), { ...o, color: STEEL });
    // emblem: downward chevron + a raised boss plate (no cross motifs)
    for (const d of [-1, 1]) u.add(at(plate(0.26, 1.5, 0.12, 0.04), X + d * 0.42, 4.15, Z + 0.17, 0, -0.18, d * 0.62), { ...o, color: CRIM });
    u.add(at(plate(1.9, 0.22, 0.12, 0.04), X, 1.55, Z + 0.17, 0, -0.18, 0), { ...o, color: CRIM });
    u.add(at(cyl(0.55, 0.62, 0.2, 8), X, 3.2, Z + 0.2, PI / 2, -0.18, 0), { ...o, color: STEEL });
    u.add(at(oct(), X, 3.2, Z + 0.33, 0, -0.18, 0, [0.3, 0.42, 0.12]), { ...o, color: PAL.teal, glow: true });
    // crack line across the lower shield (phase II motif)
    u.add(at(plate(0.9, 0.06, 0.08, 0.02), X + 0.35, 2.0, Z + 0.18, 0, -0.18, 0.5), { ...o, color: PAL.teal, glow: true });
  }
  return b.build();
}

// ============================================================================ HOLLOW TYRANT (~8 m, final boss)
function hollow(): ArtGeo {
  const b = new ArtBuilder();
  const BAS = 0x2a2830, BAS2 = 0x45404e, ARM = 0x5c5568, BONE = 0xd8ccb0, GOLD = 0xd8b050, CLOAK = 0x2a1a2e, MAG = PAL.magenta;
  // ---- legs: thick, bent-knee, armoured greaves and clawed feet
  for (const s of [-1, 1]) {
    const piv: V3 = [s * 1.05, 3.3, -0.1];
    const o: PartOpts = { color: BAS, pivot: piv, swing: 0.22, sphase: s > 0 ? 0 : PI };
    b.add(limb([s * 1.05, 3.4, -0.1], [s * 1.25, 1.95, 0.45], 0.7, 0.55, 10), o);
    b.add(limb([s * 1.25, 1.95, 0.45], [s * 1.3, 0.4, -0.05], 0.55, 0.42, 10), o);
    b.add(at(plate(1.2, 1.4, 1.0, 0.2), s * 1.28, 1.25, 0.3, 0.15), { ...o, color: ARM }); // greave
    b.add(at(shard(0.45, 1.0, 0.3), s * 1.3, 1.9, 0.75, 0.6), { ...o, color: BONE }); // knee spike
    b.add(at(plate(1.25, 0.45, 1.6, 0.15), s * 1.3, 0.22, 0.25), { ...o, color: ARM });
    for (const t of [-1, 0, 1]) b.add(at(cone(0.16, 0.7, 6), s * 1.3 + t * 0.38, 0.18, 1.25, PI / 2), { ...o, color: BONE });
    b.add(at(plate(1.3, 1.3, 0.25, 0.08), s * 1.15, 2.85, 0.55, 0.15, 0, s * 0.12), { ...o, color: ARM }); // fauld
  }
  b.add(at(plate(2.8, 0.8, 1.6, 0.25), 0, 3.4, 0), { color: BAS2 }); // pelvis girdle
  // ---- hollow torso: spine column, rib arcs, cracked core in the cavity, heavy upper chest yoke
  b.add(limb([0, 3.5, -0.45], [0, 5.9, -0.55], 0.38, 0.45, 8), { color: BAS2 });
  for (const [y, r] of [[4.1, 0.95], [4.7, 1.15], [5.25, 1.3]] as const) {
    const rib = new THREE.TorusGeometry(r, 0.13, 6, 16, PI * 1.25);
    rib.rotateX(PI / 2); rib.rotateY(-PI * 0.125 - PI / 2);
    b.add(at(rib, 0, y, -0.25), { color: BONE });
  }
  // the broken crystal core: main shard + split-off fragments
  b.add(at(oct(), 0, 5.15, 0.45, 0.25, 0.4, 0.12, [0.62, 0.95, 0.55]), { color: MAG, glow: true });
  b.add(at(shard(0.24, 0.8, 0.2), 0.55, 5.55, 0.6, 0.3, 0, -0.75), { color: MAG, glow: true });
  b.add(at(shard(0.2, 0.6, 0.16), -0.5, 4.45, 0.5, PI, 0, 0.55), { color: MAG, glow: true });
  // chest yoke split down the middle: the core shows through the breach (also from the high game camera)
  b.add(at(plate(1.75, 1.3, 2.0, 0.35), -1.2, 6.1, -0.1, 0.12, 0, 0.12), { color: ARM });
  b.add(at(plate(1.6, 1.15, 1.9, 0.33), 1.25, 6.05, -0.15, 0.1, 0, -0.2), { color: ARM });
  b.add(at(plate(1.4, 1.1, 1.0, 0.3), 0, 6.15, -0.7, 0.1), { color: BAS2 });
  b.add(at(plate(0.9, 0.7, 0.28, 0.1), -0.85, 5.65, 0.95, -0.15, 0.2, 0.35), { color: BONE }); // broken sternum shard
  b.add(at(plate(0.75, 0.07, 0.1, 0.03), 0.95, 6.2, 0.9, 0, 0, -0.6), { color: MAG, glow: true });
  // ---- sunken horned head
  b.add(at(sphere(0.55, 12, 10), 0, 6.75, 0.55, 0, 0, 0, [1, 0.9, 1.1]), { color: BAS2 });
  b.add(at(plate(1.05, 0.4, 0.9, 0.12), 0, 6.55, 0.8, 0.2), { color: BONE }); // face mask
  for (const s of [-1, 1]) {
    b.add(at(plate(0.26, 0.08, 0.08, 0.02), s * 0.22, 6.8, 1.08, 0, 0, s * 0.25), { color: MAG, glow: true });
    b.add(at(cone(0.16, 0.9, 6), s * 0.5, 7.2, 0.45, -0.3, 0, s * -0.6), { color: BONE });
  }
  // ---- fractured crown: a broken ring of shards hovering above the head, with gaps; flares back on the wind-up
  const cr: PartOpts = { color: GOLD, pivot: [0, 6.4, 0.2], raise: -0.25 };
  const CY = 7.2, CR = 1.65;
  b.add(at(new THREE.TorusGeometry(CR, 0.12, 6, 28, PI * 1.5), 0, CY, 0.05, PI / 2 - 0.1, 0, 0.75), cr);
  b.add(at(new THREE.TorusGeometry(CR * 0.8, 0.07, 6, 24, PI * 1.1), 0, CY + 0.3, 0.05, PI / 2 + 0.08, 0, 2.6), { ...cr, color: BONE });
  const hs = [1.9, 0.9, 1.4, 0, 1.6, 0.8, 1.3, 0, 1.1];
  for (let i = 0; i < 9; i++) {
    if (!hs[i]) continue; // broken teeth
    const a = (i / 9) * PI * 2, h = hs[i];
    const g = shard(0.3, h, 0.2); g.rotateX(0.32); g.rotateY(a);
    b.add(at(g, Math.sin(a) * CR, CY + Math.cos(a) * 0.12, 0.05 + Math.cos(a) * CR), { ...cr, color: i % 2 ? GOLD : BONE });
  }
  // split-off crown fragments drifting above the gaps (the crown "separates")
  b.add(at(shard(0.26, 1.0, 0.18), 1.75, 8.15, -1.1, 0.3, 0.3, -0.5), { ...cr, color: GOLD });
  b.add(at(shard(0.2, 0.7, 0.14), -1.15, 8.25, -1.5, -0.3, 0, 0.6), { ...cr, color: BONE });
  b.add(at(shard(0.14, 0.45, 0.1), -2.0, 8.0, 0.4, 0, 0, 0.9), { ...cr, color: GOLD });
  b.add(at(oct(), 0, CY + 0.35, 0.05, 0, 0, 0, [0.24, 0.36, 0.24]), { ...cr, color: MAG, glow: true });
  // ---- immense splayed arms with gauntlet claws; spread up/forward on the wind-up
  for (const s of [-1, 1]) {
    const sh: V3 = [s * 2.3, 5.95, -0.1];
    const o: PartOpts = { color: BAS, pivot: sh, swing: 0.18, sphase: s > 0 ? PI : 0, raise: -1.0 };
    // massive layered pauldron (fixed to the torso, so the shoulder silhouette stays wide)
    b.add(at(plate(2.0, 1.1, 2.0, 0.35), s * 2.45, 6.65, -0.1, 0, 0, s * -0.35), { color: ARM });
    b.add(at(plate(1.7, 0.8, 1.7, 0.3), s * 2.85, 6.05, -0.1, 0, 0, s * -0.6), { color: BAS2 });
    b.add(at(shard(0.32, 1.3, 0.3), s * 2.6, 7.05, -0.3, -0.2, 0, s * -0.45), { color: BONE });
    b.add(limb([s * 2.4, 5.8, -0.1], [s * 3.15, 3.9, 0.15], 0.6, 0.5, 10), o);
    b.add(at(sphere(0.55, 10, 8), s * 3.15, 3.9, 0.15), { ...o, color: ARM });
    b.add(limb([s * 3.15, 3.9, 0.15], [s * 3.35, 2.15, 0.6], 0.65, 0.78, 10), { ...o, color: ARM }); // gauntlet
    b.add(at(plate(0.5, 1.3, 0.9, 0.12), s * 3.6, 3.05, 0.4, 0.25, 0, s * -0.2), { ...o, color: BONE }); // forearm fin
    b.add(at(sphere(0.62, 10, 8), s * 3.38, 1.95, 0.65, 0, 0, 0, [1, 0.8, 1.1]), { ...o, color: BAS2 }); // hand
    for (const [dx, dz, rx] of [[-0.3, 0.35, 0.5], [0.05, 0.45, 0.4], [0.35, 0.3, 0.45], [s * -0.45, -0.1, 0.1]] as const) {
      b.add(at(cone(0.13, 1.0, 6), s * 3.38 + dx, 1.35, 0.7 + dz, PI - rx, 0, 0), { ...o, color: BONE });
    }
  }
  // ---- tattered cloak hanging from the yoke (uneven strips)
  const lens = [5.2, 4.3, 5.6, 4.0, 5.0, 3.6];
  for (let i = 0; i < 6; i++) {
    const x = -1.75 + i * 0.7, L = lens[i];
    b.add(at(plate(0.78, L, 0.12, 0.04), x, 6.3 - L / 2, -1.25 - (L / 6) * 0.25, -0.12, 0, x * -0.04), { color: CLOAK });
  }
  return b.build();
}

// keys are the canonical encounter names (HUD labels, saves) and must not change
export const BOSS_ART: Record<string, EnemyArt> = {
  'IRONHIDE COLOSSUS': { name: 'Ironhide Colossus', build: () => ironhide(), stride: 0.45, bob: 0.18, outline: 0.08 },
  'PRISM SOVEREIGN': { name: 'Prism Sovereign', build: () => prism(), stride: 0.6, bob: 0.08, outline: 0.06 },
  'TYRANT GUARDIAN': { name: 'Tyrant Guardian', build: () => guardian(), stride: 0.6, bob: 0.1, outline: 0.065 },
  'HOLLOW TYRANT': { name: 'Hollow Tyrant', build: () => hollow(), stride: 0.5, bob: 0.15, outline: 0.075 },
};
